import * as React from 'react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useProfileDetail } from '@/api/use-rate-collection';

// =============================================================================
// profile-detail-dialog.tsx — SF6 : pop-up « fiche profil » de l'écran de saisie (DBU §3.5).
// =============================================================================
// Ouverte au clic sur un profil de la grille, la fiche affiche EN LECTURE SEULE le descriptif du
// profil, ses niveaux disponibles, ses missions-type et ses prérequis. Contenu cohérent avec la
// zone 3.4 (référentiel). Aucune donnée saisissable ; la fermeture ne perd AUCUNE saisie en cours
// (état de la grille indépendant de cette pop-up).
//
// Modale légère écrite à la main (aucune dépendance ajoutée — pas de @radix-ui/react-dialog dans le
// lockfile figé), même patron que `confirm-dialog.tsx`, mais AVEC piège de focus complet (Tab cycle
// dans la modale) conforme WCAG 2.1 AA :
//   - role="dialog" + aria-modal="true" + aria-labelledby/aria-describedby ;
//   - focus déplacé sur le bouton Fermer à l'ouverture, restauré à l'élément précédent à la fermeture ;
//   - fermeture clavier (Échap) + clic sur l'overlay (décoratif, aria-hidden) ;
//   - piège de focus : Tab / Shift+Tab bouclent sur les éléments focusables de la modale.
//
// SENS DOUBLÉ (WCAG 1.4.1) : missions/prérequis non renseignés sont signalés par un LIBELLÉ explicite
// (« Non renseigné »), jamais par une seule nuance de couleur. Ces champs sont `null` tant qu'aucune
// source ne les peuple (parser Excel figé DDD §3.4.3 + édition admin) : la fiche ne les INVENTE jamais.

export interface ProfileDetailDialogProps {
  readonly submissionId: string;
  /** Profil dont on affiche la fiche ; `null` = pop-up fermée. */
  readonly profileCode: string | null;
  /** Libellé d'affichage connu de la grille (fallback pendant le chargement). */
  readonly fallbackLabel?: string;
  readonly onClose: () => void;
}

/** Mention honnête pour un champ facultatif non encore peuplé (SF6, sens doublé par le texte). */
const UNFILLED_NOTE = 'Non renseigné';

/** Sélecteur des éléments focusables (piège de focus WCAG). */
const FOCUSABLE =
  'a[href], button:not([disabled]), textarea, input, select, [tabindex]:not([tabindex="-1"])';

export function ProfileDetailDialog({
  submissionId,
  profileCode,
  fallbackLabel,
  onClose,
}: ProfileDetailDialogProps): React.JSX.Element | null {
  const open = profileCode !== null;
  const query = useProfileDetail(submissionId, profileCode);
  const dialogRef = React.useRef<HTMLDivElement | null>(null);
  const closeRef = React.useRef<HTMLButtonElement | null>(null);
  const previouslyFocused = React.useRef<HTMLElement | null>(null);

  React.useEffect(() => {
    if (!open) {
      return;
    }
    previouslyFocused.current = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
        return;
      }
      if (event.key !== 'Tab') {
        return;
      }
      // Piège de focus : maintient le focus DANS la modale (WCAG 2.4.3 / 2.1.2).
      const root = dialogRef.current;
      if (root === null) {
        return;
      }
      const focusables = Array.from(
        root.querySelectorAll<HTMLElement>(FOCUSABLE),
      ).filter((el) => el.offsetParent !== null || el === document.activeElement);
      if (focusables.length === 0) {
        event.preventDefault();
        return;
      }
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      const active = document.activeElement;
      if (event.shiftKey && active === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      previouslyFocused.current?.focus();
    };
  }, [open, onClose]);

  if (!open) {
    return null;
  }

  const titleId = 'profile-detail-title';
  const descId = 'profile-detail-description';
  const detail = query.data;
  const headingLabel =
    detail?.libelle ?? fallbackLabel ?? profileCode ?? 'Fiche profil';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-foreground/40"
        aria-hidden="true"
        onClick={onClose}
      />
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
        className={cn(
          'relative z-10 w-full max-w-lg space-y-4 rounded-lg border border-border',
          'max-h-[85vh] overflow-y-auto bg-card p-6 text-card-foreground shadow-lg',
        )}
      >
        <div className="space-y-1">
          <h2 id={titleId} className="text-lg font-semibold tracking-tight">
            {headingLabel}
          </h2>
          <p id={descId} className="font-mono text-xs text-muted-foreground">
            {/* Code toujours visible (traçabilité). */}
            {profileCode}
            {detail?.famille ? ` · ${detail.famille}` : ''}
            {detail?.sousFamille ? ` / ${detail.sousFamille}` : ''}
          </p>
        </div>

        {query.isPending && (
          <p className="text-sm text-muted-foreground" role="status">
            Chargement de la fiche profil…
          </p>
        )}

        {query.isError && (
          <p className="text-sm text-destructive" role="alert">
            La fiche profil n'a pas pu être chargée. Fermez et réessayez.
          </p>
        )}

        {detail && (
          <dl className="space-y-4">
            <Field label="Description">
              <FieldText value={detail.description} />
            </Field>

            <div>
              <dt className="text-sm font-medium text-foreground">
                Niveaux disponibles
              </dt>
              <dd className="mt-1">
                {detail.availableLevels.length > 0 ? (
                  <ul className="flex flex-wrap gap-1.5">
                    {detail.availableLevels.map((level) => (
                      <li
                        key={level}
                        className="rounded-md border border-border bg-muted px-2 py-0.5 text-xs text-foreground"
                      >
                        {level}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <UnfilledText />
                )}
              </dd>
            </div>

            <Field label="Missions types">
              <FieldText value={detail.missions} />
            </Field>

            <Field label="Prérequis spécifiques par niveau">
              <FieldText value={detail.prerequis} />
            </Field>
          </dl>
        )}

        <div className="flex justify-end">
          <Button ref={closeRef} type="button" variant="outline" onClick={onClose}>
            Fermer
          </Button>
        </div>
      </div>
    </div>
  );
}

/** Un champ libellé + contenu de la fiche (dt/dd sémantiques). */
function Field({
  label,
  children,
}: {
  readonly label: string;
  readonly children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div>
      <dt className="text-sm font-medium text-foreground">{label}</dt>
      <dd className="mt-1 whitespace-pre-line text-sm text-muted-foreground">
        {children}
      </dd>
    </div>
  );
}

/** Rend un texte facultatif, ou la mention honnête « Non renseigné » (sens doublé par le libellé). */
function FieldText({
  value,
}: {
  readonly value: string | null;
}): React.JSX.Element {
  const trimmed = value?.trim() ?? '';
  if (trimmed.length === 0) {
    return <UnfilledText />;
  }
  return <span>{trimmed}</span>;
}

/** Mention « Non renseigné » — libellé explicite (jamais un code couleur seul). */
function UnfilledText(): React.JSX.Element {
  return <span className="italic text-muted-foreground">{UNFILLED_NOTE}</span>;
}
